import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import React from 'react'
import * as style from 'src/app/BulkInvite/components/PrepareCsvCard.style'
import { useBulkInviteStrings } from 'src/app/BulkInvite/useBulkInviteStrings'
import { downloadTemplate } from 'src/app/BulkInvite/utils/downloadCsv'

/**
 * "Preparing your CSV" sidebar card for the upload step. Replaces the per-HRIS
 * export accordion with a static reference of the columns we look for plus a
 * format/size/rows stat row.
 *
 * NOTE: Keep this in sync with the columns the import supports: Email
 * (required), First name, Last name, Phone, and Role.
 */
const PrepareCsvCard: React.FC = () => {
  const strings = useBulkInviteStrings()

  const fields: Array<{
    name: string
    hint: string
    required: boolean
    testId: string
  }> = [
    {
      name: strings.prepareFieldEmail,
      hint: strings.prepareFieldEmailHint,
      required: true,
      testId: 'email',
    },
    {
      name: strings.prepareFieldFirstName,
      hint: strings.prepareFieldFirstNameHint,
      required: false,
      testId: 'firstName',
    },
    {
      name: strings.prepareFieldLastName,
      hint: strings.prepareFieldLastNameHint,
      required: false,
      testId: 'lastName',
    },
    {
      name: strings.prepareFieldPhone,
      hint: strings.prepareFieldPhoneHint,
      required: false,
      testId: 'phone',
    },
    {
      name: strings.prepareFieldRole,
      hint: strings.prepareFieldRoleHint,
      required: false,
      testId: 'role',
    },
  ]

  const stats: Array<{ value: string; label: string }> = [
    { value: strings.prepareStatFormat, label: strings.prepareStatFormatLabel },
    { value: strings.prepareStatSize, label: strings.prepareStatSizeLabel },
    { value: strings.prepareStatRows, label: strings.prepareStatRowsLabel },
  ]

  return (
    <section css={style.root} data-testid="bulkInvitePrepareCard">
      <h3 css={style.title}>{strings.prepareCardTitle}</h3>

      <p css={style.body}>
        {strings.prepareCardBody(
          <strong css={style.bodyEmphasis}>
            {strings.prepareCardCsvEmphasis}
          </strong>,
        )}
      </p>

      <div css={style.templateRow}>
        <span css={style.templatePrompt}>{strings.uploadStarterPrompt}</span>
        <Button
          variant="text"
          onClick={downloadTemplate}
          testId="bulkInviteUploadTemplate"
        >
          {strings.uploadDownloadTemplate}
        </Button>
      </div>

      <p css={style.sectionLabel}>{strings.prepareFieldsLabel}</p>

      <ul css={style.fieldList}>
        {fields.map((field) => (
          <li
            key={field.testId}
            css={style.fieldRow}
            data-testid={`bulkInvitePrepareField-${field.testId}`}
          >
            {/* Decorative — required/optional is conveyed by the Pill text, not
                by the checkmark color alone. */}
            <span aria-hidden="true" css={style.fieldIcon}>
              <CheckmarkCircleIcon width={18} height={18} />
            </span>
            <span css={style.fieldBody}>
              <span css={style.fieldHeader}>
                <span css={style.fieldName}>{field.name}</span>
                {field.required ? (
                  <Pill
                    className={style.staticPillClassName}
                    color="highlight"
                    size="small"
                  >
                    {strings.prepareRequiredPill}
                  </Pill>
                ) : (
                  <Pill className={style.staticPillClassName} size="small">
                    {strings.prepareOptionalText}
                  </Pill>
                )}
              </span>
              <span css={style.fieldHint}>{field.hint}</span>
            </span>
          </li>
        ))}
      </ul>

      <div css={style.statRow}>
        {stats.map((stat) => (
          <div key={stat.label} css={style.stat}>
            <span css={style.statValue}>{stat.value}</span>
            <span css={style.statLabel}>{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

export default PrepareCsvCard
